import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useFieldMappingContext } from '~/src/pages/Populate/FieldMappingProvider';
import type {
  FieldMappingMetadata,
  FieldType,
  StringFieldType,
} from '~/src/entities/project/types';

type UseIsValueAppliedParams = {
  metadata?: FieldMappingMetadata;
  fieldId: string;
  variableType: FieldType | StringFieldType;
  isRoleCard: boolean;
  contactId?: number;
};

/**
 * Checks whether the current form value for a field matches the Clio Manage
 * value from its active mapping. Returns false when there is no active mapping.
 */
export const useIsValueApplied = ({
  metadata,
  fieldId,
  variableType,
  isRoleCard,
  contactId,
}: UseIsValueAppliedParams): boolean => {
  const { getOptionsLookup } = useFieldMappingContext();
  const { watch } = useFormContext();

  const currentFormValue = watch(fieldId);

  return useMemo(() => {
    if (!metadata?.source_field) return false;

    const mappingAttribute = metadata.source_field.split('.').at(-1);
    if (!mappingAttribute) return false;

    const lookup = getOptionsLookup(variableType, isRoleCard, contactId);
    const mappingOption = lookup.get(mappingAttribute);
    if (!mappingOption) return false;

    const formValue = currentFormValue || '';
    const mappingValue = mappingOption.value || '';

    return formValue === mappingValue;
  }, [
    metadata?.source_field,
    getOptionsLookup,
    variableType,
    isRoleCard,
    contactId,
    currentFormValue,
  ]);
};
